"use client";

import { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { motion, useInView } from "framer-motion";
import axios from "../../../../components/axios";
import {
  FaClipboardList,
  FaProjectDiagram,
  FaTint,
  FaUtensils,
  FaHandHoldingUsd,
  FaUsers,
  FaChevronLeft,
  FaChevronRight,
  FaHome,
  FaGraduationCap,
  FaMedkit,
  FaTools,
  FaLeaf,
  FaHandshake,
} from "react-icons/fa";
import DividerLine from "../../../../components/DividerLine";

const AchievementsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [achievementsData, setAchievementsData] = useState([]);
  const [visibleCards, setVisibleCards] = useState(3);
  const [isLoading, setIsLoading] = useState(true);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { threshold: 0.2, once: true });

  // Determine number of visible cards based on screen size
  const updateVisibleCards = () => {
    const width = window.innerWidth;
    if (width <= 640) {
      setVisibleCards(1);
    } else if (width <= 1024) {
      setVisibleCards(2);
    } else {
      setVisibleCards(3);
    }
  };

  useEffect(() => {
    updateVisibleCards();
    window.addEventListener("resize", updateVisibleCards);
    return () => window.removeEventListener("resize", updateVisibleCards);
  }, []);

  useEffect(() => {
    if (isInView) {
      setIsVisible(true);
    }
  }, [isInView]);

  // Map API data to achievements structure
  const mapDataToAchievements = (data) => {
    const iconMap = {
      programs: FaClipboardList,
      projects: FaProjectDiagram,
      wash: FaTint,
      nutrition: FaUtensils,
      support: FaHandHoldingUsd,
      impact: FaUsers,
      housing: FaHome,
      education: FaGraduationCap,
      healthcare: FaMedkit,
      training: FaTools,
      environment: FaLeaf,
      collaboration: FaHandshake,
    };

    const colorSchemes = {
      programs: { color: "#10b981", bgColor: "#f0fdf4" },
      projects: { color: "#3b82f6", bgColor: "#eff6ff" },
      wash: { color: "#06b6d4", bgColor: "#f0f9ff" },
      nutrition: { color: "#f59e0b", bgColor: "#fffbeb" },
      support: { color: "#8b5cf6", bgColor: "#faf5ff" },
      impact: { color: "#ef4444", bgColor: "#fef2f2" },
      housing: { color: "#84cc16", bgColor: "#f7fee7" },
      education: { color: "#f97316", bgColor: "#fff7ed" },
      healthcare: { color: "#ec4899", bgColor: "#fdf2f8" },
      training: { color: "#6366f1", bgColor: "#eef2ff" },
      environment: { color: "#059669", bgColor: "#ecfdf5" },
      collaboration: { color: "#7c3aed", bgColor: "#f3e8ff" },
    };

    return data.map((item, index) => {
      const category = item.title.toLowerCase().includes("program")
        ? "programs"
        : item.title.toLowerCase().includes("project")
        ? "projects"
        : item.title.toLowerCase().includes("wash")
        ? "wash"
        : item.title.toLowerCase().includes("food")
        ? "nutrition"
        : item.title.toLowerCase().includes("cash")
        ? "support"
        : item.title.toLowerCase().includes("impact")
        ? "impact"
        : item.title.toLowerCase().includes("shelter")
        ? "housing"
        : item.title.toLowerCase().includes("education")
        ? "education"
        : item.title.toLowerCase().includes("health")
        ? "healthcare"
        : item.title.toLowerCase().includes("skill")
        ? "training"
        : item.title.toLowerCase().includes("green")
        ? "environment"
        : item.title.toLowerCase().includes("partner")
        ? "collaboration"
        : "programs";

      return {
        id: index + 1,
        icon: iconMap[category] || FaClipboardList,
        number: item.description.match(/\d+/)?.[0] || "0",
        title: item.title,
        description: item.description,
        category: category.charAt(0).toUpperCase() + category.slice(1),
        ...colorSchemes[category],
      };
    });
  };

  // Fetch data from API
  useEffect(() => {
    setIsLoading(true);
    const fetchData = async () => {
      try {
        const { data } = await axios.get("achievements/getachievements");
        setAchievementsData(mapDataToAchievements(data));
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching achievements:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const totalSlides = Math.ceil(achievementsData.length / visibleCards);

  // Auto-play functionality
  useEffect(() => {
    if (!isPlaying || achievementsData.length <= visibleCards) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % totalSlides);
    }, 3000);

    return () => clearInterval(interval);
  }, [
    isPlaying,
    totalSlides,
    achievementsData.length,
    visibleCards,
    currentIndex,
  ]);

  const nextSlide = () => {
    setIsPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % totalSlides);
    setTimeout(() => setIsPlaying(true), 3000);
  };

  const prevSlide = () => {
    setIsPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
    setTimeout(() => setIsPlaying(true), 3000);
  };

  const goToSlide = (index) => {
    setIsPlaying(false);
    setCurrentIndex(index % totalSlides);
    setTimeout(() => setIsPlaying(true), 3000);
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const translateX = -currentIndex * 100;

  return (
    <AchievementsContainer ref={sectionRef}>
      <ContentWrapper>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <HeaderSection>
            {isLoading ? (
              <>
                <div
                  style={{
                    width: "300px",
                    height: "40px",
                    background: "#e2e8f0",
                    margin: "0 auto 1rem",
                    borderRadius: "4px",
                  }}
                />
                <DividerLine />
                <div
                  style={{
                    width: "400px",
                    height: "24px",
                    background: "#e2e8f0",
                    margin: "0 auto",
                    borderRadius: "4px",
                    maxWidth: "100%",
                  }}
                />
              </>
            ) : (
              <>
                <SectionTitle>Our Impact & Achievements</SectionTitle>
                <DividerLine />
                <SectionDescription>
                  Measuring our commitment to sustainable development and
                  community empowerment
                </SectionDescription>
              </>
            )}
          </HeaderSection>
        </motion.div>

        <CarouselContainer>
          <CarouselWrapper>
            <CardsTrack
              style={{
                transform: `translateX(${translateX}%)`,
                transition: "transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              {isLoading
                ? Array.from({ length: visibleCards }).map((_, index) => (
                    <SkeletonCard key={`skeleton-${index}`}>
                      <CardHeader>
                        <SkeletonBadge />
                        <SkeletonIcon />
                      </CardHeader>
                      <NumberSection>
                        <SkeletonNumber />
                        <NumberLabel>+</NumberLabel>
                      </NumberSection>
                      <ContentBody>
                        <SkeletonTitle />
                        <SkeletonDescription />
                      </ContentBody>
                      <SkeletonProgress />
                    </SkeletonCard>
                  ))
                : achievementsData.map((achievement) => {
                    const IconComponent = achievement.icon;
                    return (
                      <AchievementCard
                        key={achievement.id}
                        bgColor={achievement.bgColor}
                        onMouseEnter={() => setIsPlaying(false)}
                        onMouseLeave={() => setIsPlaying(true)}
                      >
                        <CardHeader>
                          <CategoryBadge color={achievement.color}>
                            {achievement.category}
                          </CategoryBadge>
                          <IconWrapper color={achievement.color}>
                            <IconComponent />
                          </IconWrapper>
                        </CardHeader>

                        <NumberSection>
                          <ImpactNumber color={achievement.color}>
                            <CountUpNumber
                              target={Number.parseInt(achievement.number)}
                              isVisible={isVisible}
                              cardId={achievement.id}
                            />
                          </ImpactNumber>
                          <NumberLabel>+</NumberLabel>
                        </NumberSection>

                        <ContentBody>
                          <CardTitle>{achievement.title}</CardTitle>
                          <CardDescription>
                            {achievement.description}
                          </CardDescription>
                        </ContentBody>

                        <ProgressBar>
                          <ProgressFill
                            color={achievement.color}
                            width={Math.min(
                              (Number.parseInt(achievement.number) / 200) * 100,
                              100
                            )}
                          />
                        </ProgressBar>
                      </AchievementCard>
                    );
                  })}
            </CardsTrack>
          </CarouselWrapper>

          {achievementsData.length > visibleCards && (
            <>
              <CarouselControls>
                <IndicatorsContainer>
                  {isLoading ? (
                    <>
                      <SkeletonNavButton left />
                      {Array.from({ length: 5 }).map((_, index) => (
                        <SkeletonIndicator key={`indicator-${index}`} />
                      ))}
                      <SkeletonNavButton />
                    </>
                  ) : (
                    <>
                      {achievementsData.length > visibleCards && (
                        <NavButton left onClick={prevSlide}>
                          <FaChevronLeft />
                        </NavButton>
                      )}
                      {Array.from({ length: totalSlides }).map((_, index) => (
                        <Indicator
                          key={index}
                          active={index === currentIndex}
                          onClick={() => goToSlide(index)}
                        />
                      ))}
                      {achievementsData.length > visibleCards && (
                        <NavButton onClick={nextSlide}>
                          <FaChevronRight />
                        </NavButton>
                      )}
                    </>
                  )}
                </IndicatorsContainer>
              </CarouselControls>

              <ProgressContainer>
                <AutoProgressBar>
                  {isLoading ? (
                    <div
                      style={{
                        background: "#e2e8f0",
                        height: "100%",
                        width: "100%",
                      }}
                    />
                  ) : (
                    <AutoProgressFill
                      initial={{ width: "0%" }}
                      animate={{ width: isPlaying ? "100%" : "0%" }}
                      transition={{
                        duration: isPlaying ? 3 : 0,
                        ease: "linear",
                      }}
                      key={currentIndex}
                    />
                  )}
                </AutoProgressBar>
              </ProgressContainer>
            </>
          )}
        </CarouselContainer>
      </ContentWrapper>
    </AchievementsContainer>
  );
};

const CountUpNumber = ({ target, isVisible, cardId }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if (!isVisible || hasAnimated) return;

    let start = 0;
    const increment = target / 30;
    const counter = setInterval(() => {
      start += increment;
      if (start >= target) {
        setCount(target);
        setHasAnimated(true);
        clearInterval(counter);
      } else {
        setCount(Math.floor(start));
      }
    }, 50);

    return () => clearInterval(counter);
  }, [isVisible, target, hasAnimated]);

  return <span>{count.toLocaleString()}</span>;
};

export default AchievementsSection;

// Styled Components
const AchievementsContainer = styled.section`
  padding: 5rem 0;
  background-color: #fff;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: clamp(2.5rem, 4vw, 3.5rem);
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 1rem 0;
  letter-spacing: -0.02em;
  line-height: 1.2;
`;

const SectionDescription = styled.p`
  font-size: clamp(1.1rem, 1.5vw, 1.3rem);
  color: #64748b;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const CarouselContainer = styled.div`
  position: relative;
  margin-bottom: 3rem;
`;

const CarouselWrapper = styled.div`
  position: relative;
  overflow: hidden;
  margin-bottom: 2rem;
  width: 100%;
  max-width: 100%;
  padding: 0;
  box-sizing: border-box;
`;

const CardsTrack = styled.div`
  display: flex;
  width: ${(props) => (props.totalCards / 3) * 100}%;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);

  & > *:nth-child(4n) {
    margin-left: 0;
  }
`;

const AchievementCard = styled.div`
  flex: 0 0 calc(33.333% - 0.666rem);
  background: ${(props) => props.bgColor};
  border-radius: 16px;
  padding: 1.5rem;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  cursor: pointer;
  overflow: hidden;
  height: 280px;
  display: flex;
  flex-direction: column;
  margin-right: 15px;
  box-sizing: border-box;

  &:hover {
    transform: translateY(-4px);
  }

  @media (max-width: 1024px) {
    flex: 0 0 calc(50% - 0.5rem);
    margin-right: 10px;
  }

  @media (max-width: 640px) {
    flex: 0 0 100%;
    margin-right: 0px;
    height: 160px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const CategoryBadge = styled.span`
  background: ${(props) => props.color}15;
  color: ${(props) => props.color};
  padding: 0.4rem 0.8rem;
  border-radius: 16px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const IconWrapper = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${(props) => props.color}20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.color};
  font-size: 1.2rem;
`;

const NumberSection = styled.div`
  display: flex;
  align-items: baseline;
  margin-bottom: 1rem;
`;

const ImpactNumber = styled.div`
  font-size: clamp(2rem, 3vw, 2.5rem);
  font-weight: 800;
  color: ${(props) => props.color};
  line-height: 1;
  font-family: "Arial", sans-serif;
`;

const NumberLabel = styled.span`
  font-size: 1.2rem;
  color: #64748b;
  margin-left: 0.25rem;
  font-weight: 600;
`;

const ContentBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const CardTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 0.5rem 0;
  line-height: 1.3;
`;

const CardDescription = styled.p`
  font-size: 0.85rem;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
  flex: 1;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 3px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 2px;
  overflow: hidden;
  margin-top: 1rem;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${(props) => props.color};
  width: ${(props) => props.width}%;
  border-radius: 2px;
  transition: width 2s ease-out;
`;

const NavButton = styled.button`
  transform: translateY(-35%);
  ${(props) => (props.left ? "left: 1rem;" : "right: 1rem;")}
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  color: #1e293b;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  z-index: 3;

  &:hover {
    background: white;
    transform: translateY(-50%) scale(1.1);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 1rem;
    ${(props) => (props.left ? "left: 0.5rem;" : "right: 0.5rem;")}
  }
`;

const CarouselControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const IndicatorsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Indicator = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: ${(props) => (props.active ? "#10b981" : "#cbd5e1")};
  cursor: pointer;
  transition: all 0.3s ease;
  transform: ${(props) => (props.active ? "scale(1.2)" : "scale(1)")};

  &:hover {
    transform: scale(1.2);
  }
`;

const ProgressContainer = styled.div`
  margin-bottom: 2rem;
`;

const AutoProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  overflow: hidden;
`;

const AutoProgressFill = styled(motion.div)`
  height: 100%;
  background: linear-gradient(90deg, #10b981, #3b82f6);
  border-radius: 2px;
`;

// Skeleton Components
const SkeletonCard = styled.div`
  flex: 0 0 calc(33.333% - 0.666rem);
  background: #e2e8f0;
  border-radius: 16px;
  padding: 1.5rem;
  height: 280px;
  margin-right: 15px;
  position: relative;
  overflow: hidden;

  @media (max-width: 1024px) {
    flex: 0 0 calc(50% - 0.5rem);
    margin-right: 10px;
  }

  @media (max-width: 640px) {
    flex: 0 0 100%;
    margin-right: 0px;
    height: 160px;
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.6),
      transparent
    );
    animation: shimmer 1.5s infinite;
  }

  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`;

const SkeletonBadge = styled.div`
  width: 80px;
  height: 24px;
  background: #cbd5e1;
  border-radius: 16px;
  margin-bottom: 1rem;
`;

const SkeletonIcon = styled.div`
  width: 40px;
  height: 40px;
  background: #cbd5e1;
  border-radius: 10px;
`;

const SkeletonNumber = styled.div`
  width: 120px;
  height: 40px;
  background: #cbd5e1;
  margin-bottom: 1rem;
`;

const SkeletonTitle = styled.div`
  width: 80%;
  height: 20px;
  background: #cbd5e1;
  margin-bottom: 0.5rem;
`;

const SkeletonDescription = styled.div`
  width: 100%;
  height: 40px;
  background: #cbd5e1;
  margin-bottom: 1rem;
`;

const SkeletonProgress = styled.div`
  width: 100%;
  height: 3px;
  background: #cbd5e1;
  margin-top: auto;
`;

const SkeletonIndicator = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #e2e8f0;
`;

const SkeletonNavButton = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #e2e8f0;

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
  }
`;
